const seoConfig = {
  site: {
    name: 'Civic Engage',
    url: 'https://civicengage.me',
    description: 'Track federal legislation, connect with your representatives, and make your voice heard. Civic Engage makes it easy to stay informed about bills in Congress and take action on issues that matter to you.',
    tagline: 'Empowering citizens to understand legislation and take action',
    twitter: '@civicengage',
    foundingDate: '2024',
    contactEmail: 'contact@civicengage.me',
  },

  organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Civic Engage',
    'url': 'https://civicengage.me',
    'logo': 'https://civicengage.me/logo512.png',
    'description': 'A civic engagement platform helping Americans track federal legislation and connect with their representatives',
    'foundingDate': '2024',
    'areaServed': {
      '@type': 'Country',
      'name': 'United States'
    },
    'serviceType': 'Civic Information Service',
    'sameAs': [
      'https://twitter.com/civicengage',
      'https://github.com/civicengage'
    ]
  },

  pages: {
    home: {
      title: 'Civic Engage | Track Federal Legislation & Connect with Representatives',
      description: 'Stay informed about federal bills in Congress. Find your representatives, understand legislation with AI summaries, and take action on issues that matter to you.',
      keywords: 'federal legislation, congress bills, US representatives, senators, civic engagement, government transparency, democracy',
      path: '/',
    },
    bills: {
      title: 'Federal Bills & Legislation | Civic Engage',
      description: 'Browse and search current federal bills in the U.S. Congress. Get AI-powered summaries, track bill progress, and understand how legislation affects you.',
      keywords: 'federal bills, congress legislation, house bills, senate bills, bill tracker, legislative updates',
      path: '/bills',
    },
    legislators: {
      title: 'U.S. Senators & Representatives | Civic Engage',
      description: 'Find your U.S. senators and representatives. Get contact information, view voting records, and connect with your elected officials on issues that matter.',
      keywords: 'US senators, house representatives, congress members, find my representative, contact congress, elected officials',
      path: '/legislators',
    },
    resources: {
      title: 'Civic Engagement Resources | Civic Engage',
      description: 'Learn how bills become law, understand the legislative process, and discover ways to make your voice heard in government.',
      keywords: 'civic education, legislative process, how bills become law, civic participation, democracy resources',
      path: '/resources',
    },
  },

  getBreadcrumbs: (path, title) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': 'https://civicengage.me/'
      },
      ...(path !== '/' ? [{
        '@type': 'ListItem',
        'position': 2,
        'name': title,
        'item': `https://civicengage.me${path}`
      }] : [])
    ]
  }),

  getGovernmentServiceSchema: () => ({
    '@context': 'https://schema.org',
    '@type': 'GovernmentService',
    'name': 'Federal Legislation Tracking',
    'description': 'Track federal bills and legislation in the U.S. Congress',
    'provider': {
      '@type': 'Organization',
      'name': 'Civic Engage'
    },
    'areaServed': {
      '@type': 'Country',
      'name': 'United States'
    },
    'audience': {
      '@type': 'Audience',
      'audienceType': 'U.S. Citizens and Residents'
    }
  }),

  getArticleSchema: (bill) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': bill.title,
    'description': bill.summary || bill.ai_description,
    'datePublished': bill.introduced_date,
    'dateModified': bill.latest_action_date,
    'author': {
      '@type': 'Organization',
      'name': 'U.S. Congress'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Civic Engage',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://civicengage.me/logo512.png'
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://civicengage.me/bills/${bill.bill_id}`
    }
  }),

  getPersonSchema: (member) => ({
    '@context': 'https://schema.org',
    '@type': 'Person',
    'name': member.name,
    'jobTitle': member.title === 'senator' ? 'U.S. Senator' : 'U.S. Representative',
    'memberOf': {
      '@type': 'Organization',
      'name': 'United States Congress'
    },
    'address': {
      '@type': 'PostalAddress',
      'addressRegion': member.state,
      'addressCountry': 'US'
    },
    'affiliation': member.party,
    'url': member.website,
    ...(member.image_url && {
      'image': member.image_url
    })
  }),

  getWebsiteSchema: () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'Civic Engage',
    'url': 'https://civicengage.me',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': 'https://civicengage.me/bills?search={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  }),

  getFAQSchema: (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  })
};

export default seoConfig;
